import type { PaginatedResponse, TeamPlayer } from "../../../types";
import {
  finalizeLineupForSave,
  type LineupPlayerSelection,
} from "../../../components/lineup/useLineupDnd";
import { apiClient } from "../../../lib/api";
import { formatPlayerName } from "../../../lib/nameFormat";

/**
 * Shared match-lineup snapshot helpers (PMA-COCK-171). Originally private to
 * `PreMatchLineupPanel.tsx` (EPIC-08 Phase 4 / PMA-COCK-090) — factored out
 * so the substitution flow's "rest of the squad" list (which needs the same
 * team roster + current lineup) and the live bench-only lineup panel share
 * exactly one fetch/normalize/save implementation instead of drifting apart.
 */

const ROSTER_PAGE_SIZE = 100;

export interface RawLineupPlayer {
  player_id: string;
  player_name?: string;
  jersey_number: number;
  position: string;
  is_starter?: boolean;
  is_captain?: boolean;
}

export interface MatchLineupSnapshot {
  _id?: string;
  home_team?: { lineup?: RawLineupPlayer[] };
  away_team?: { lineup?: RawLineupPlayer[] };
}

/** Full club roster for a team — NOT the matchday lineup. Callers must fetch
 * this once (on open/mount) and reuse it; never call this per keystroke. */
export async function fetchTeamRoster(teamId: string): Promise<TeamPlayer[]> {
  const normalizedTeamId = (teamId || "").trim();
  const roster: TeamPlayer[] = [];
  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const resp = await apiClient.get<
      PaginatedResponse<TeamPlayer> | TeamPlayer[]
    >(`/teams/${normalizedTeamId}/players`, {
      params: { page, page_size: ROSTER_PAGE_SIZE },
    });
    if (Array.isArray(resp)) {
      roster.push(
        ...resp.map((player) => ({
          ...player,
          player_name: formatPlayerName(player.player_name),
        })),
      );
      hasMore = false;
    } else if (resp && Array.isArray(resp.items)) {
      roster.push(
        ...resp.items.map((player) => ({
          ...player,
          player_name: formatPlayerName(player.player_name),
        })),
      );
      const total = resp.total ?? resp.items.length;
      hasMore = roster.length < total;
    } else {
      hasMore = false;
    }
    page += 1;
  }
  return roster;
}

// Mirrors MatchesManager.tsx's normalizeLineup exactly, including is_captain
// preservation. Deliberately reads the raw CRUD-router match snapshot
// (fetchMatchLineupSnapshot below), NOT the cockpit's own `match` prop — the
// logger router never returns is_captain and the cockpit's Player/
// coercePlayers normalization has no such field, so seeding from `match`
// would silently clear every captain flag on the first save.
export function normalizeLineup(
  lineup?: RawLineupPlayer[] | null,
): LineupPlayerSelection[] {
  return (lineup ?? []).map((player) => ({
    player_id: player.player_id,
    player_name: formatPlayerName(player.player_name || player.player_id),
    position: player.position as LineupPlayerSelection["position"],
    jersey_number: player.jersey_number,
    is_starter: player.is_starter ?? true,
    is_captain: player.is_captain ?? false,
  }));
}

export async function fetchMatchLineupSnapshot(
  matchId: string,
): Promise<MatchLineupSnapshot> {
  return apiClient.get<MatchLineupSnapshot>(`/matches/${matchId}`);
}

/** `snapshotId` is the raw Mongo `_id` from `fetchMatchLineupSnapshot`, NOT
 * the cockpit's app-level `match.id` (OQ-EPIC08-03). */
export async function patchMatchLineups(
  snapshotId: string,
  homeLineup: LineupPlayerSelection[],
  awayLineup: LineupPlayerSelection[],
): Promise<void> {
  await apiClient.patch(`/matches/${snapshotId}/lineups`, {
    home_team_lineup: finalizeLineupForSave(homeLineup),
    away_team_lineup: finalizeLineupForSave(awayLineup),
  });
}

export type { LineupPlayerSelection } from "../../../components/lineup/useLineupDnd";
